<template>
	<v-card class="comment-edit-form px-3 elevation-0 py-0" style="width: 100%">
		<v-form ref="form" v-model="valid" lazy-validation @submit.prevent="validateAndSubmit">
			<div class="d-flex comment-edit-form__text-outer pt-4 mb-3">
				<div
					ref="editableDiv"
					class="comment-edit-form__editable"
					:class="
						fields.content && fields.content.trim()
							? 'comment-edit-form__editable--active'
							: ''
					"
					contenteditable="true"
					@input="onEditableInput"
				></div>

				<div class="comment-edit-form__buttons">
					<button
						:disabled="isSubmitDisabled"
						type="submit"
						class="comment-edit-form__buttons__save"
					>
						Save
					</button>

					<button
						type="button"
						class="comment-edit-form__buttons__cancel"
						@click="$emit('hideEditInput')"
					>
						Cancel
					</button>
				</div>
			</div>

			<p v-for="(error, index) in errors" :key="index" class="red--text">
				{{ error }}
			</p>
		</v-form>

		<CommentMentionBar
			v-if="isMentionActive"
			:searchString="activeMentionSearch"
			:mentions="mentions"
			@setMention="setMention"
		/>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import { ruleRequired } from '@/utils/rules'
import CommentMentionBar from '@/components/CommentMentionBar.vue'

export default {
	name: 'CommentEditForm',

	components: {
		CommentMentionBar,
	},

	mixins: [Form],

	props: {
		comment: {
			type: Object,
			default: () => ({}),
		},
	},

	data: () => ({
		fields: {
			content: '',
		},
		mentions: [],
	}),

	computed: {
		...mapGetters('auth', ['isAuthenticated', 'profile']),

		isSubmitDisabled() {
			return !this.fields.content || !this.fields.content.trim()
		},

		isMentionActive() {
			if (!this.fields.content) return false
			return !!this.getActiveMentionRange()
		},
		activeMentionSearch() {
			const range = this.getActiveMentionRange()
			return range ? range.searchText : ''
		},

		rules() {
			return {
				content: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => !!v.trim().length || 'Text cannot be empty',
				],
			}
		},
	},

	watch: {
		'fields.content': {
			handler(newVal) {
				if (!newVal) {
					this.mentions = []
					return
				}
				this.mentions = this.mentions.filter((mention) => {
					const normalizedUsername = mention.visible_username.replace(/\s+/g, '')
					const regex = new RegExp(
						`(^|\\s)@${this.escapeRegex(normalizedUsername)}(?=\\s|$)`,
						'g'
					)
					return regex.test(newVal)
				})
			},
		},
	},

	created() {
		const content = this.comment.content || ''
		const mentionRegex = /@\[([0-9]+)\|([^\]]+)\]/g
		const mentions = []

		const parsedContent = content.replace(mentionRegex, (match, userId, username) => {
			mentions.push({ user_id: userId, visible_username: username })
			return `@${username.replace(/\s+/g, '')}`
		})

		this.fields = {
			...this.fields,
			...this.comment,
			content: parsedContent,
		}

		this.mentions = mentions
	},

	mounted() {
		this.syncDivFromContent(false)
	},

	methods: {
		...mapActions('post', ['editComment']),
		...mapActions('auth', ['showAlert']),
		getActiveMentionRange() {
			const content = this.fields.content || ''
			const match = content.match(/(^|\s)@([^\s@]*)$/)
			if (!match) return null
			const atIndex = match.index + match[1].length
			return { atIndex, searchText: match[2] }
		},
		renderHTML(text) {
			if (!text) return ''
			const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
			let result = escaped
			this.mentions.forEach((mention) => {
				const normalizedUsername = mention.visible_username.replace(/\s+/g, '')
				const regex = new RegExp(
					`@(${this.escapeRegex(normalizedUsername)})(?=\\s|$|&)`,
					'g'
				)
				result = result.replace(
					regex,
					`<span class="mention-chip" contenteditable="false">@$1</span>`
				)
			})

			return result
		},

		escapeRegex(str) {
			return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		},
		syncDivFromContent(restoreCursor = true) {
			const div = this.$refs.editableDiv
			if (!div) return

			const savedPos = restoreCursor ? this.saveCursorPosition(div) : null
			div.innerHTML = this.renderHTML(this.fields.content)
			if (restoreCursor && savedPos !== null) {
				this.restoreCursorPosition(div, savedPos)
			}
		},

		onEditableInput(e) {
			this.fields.content = e.target.innerText
		},

		setMention(user) {
			const activeMention = this.getActiveMentionRange()
			if (!activeMention) return

			const normalizedUsername = user.visible_username.replace(/\s+/g, '')
			const before = this.fields.content.slice(0, activeMention.atIndex)
			this.fields.content = `${before}@${normalizedUsername} `
			this.mentions.push(user)

			this.$nextTick(() => {
				this.syncDivFromContent(false)
				this.setCursorToEnd(this.$refs.editableDiv)
			})
		},
		saveCursorPosition(el) {
			const sel = window.getSelection()
			if (!sel || sel.rangeCount === 0) return null

			const range = sel.getRangeAt(0)
			const preRange = range.cloneRange()
			preRange.selectNodeContents(el)
			preRange.setEnd(range.endContainer, range.endOffset)
			return preRange.toString().length
		},
		restoreCursorPosition(el, offset) {
			const sel = window.getSelection()
			if (!sel) return

			const range = document.createRange()
			let currentOffset = 0
			let found = false

			const walk = (node) => {
				if (found) return
				if (node.nodeType === Node.TEXT_NODE) {
					const len = node.textContent.length
					if (currentOffset + len >= offset) {
						range.setStart(node, offset - currentOffset)
						range.collapse(true)
						found = true
					} else {
						currentOffset += len
					}
				} else {
					for (const child of node.childNodes) {
						walk(child)
					}
				}
			}

			walk(el)

			if (!found) {
				range.selectNodeContents(el)
				range.collapse(false)
			}

			sel.removeAllRanges()
			sel.addRange(range)
		},

		setCursorToEnd(el) {
			const range = document.createRange()
			const sel = window.getSelection()
			range.selectNodeContents(el)
			range.collapse(false)
			sel.removeAllRanges()
			sel.addRange(range)
			el.focus()
		},
		submitForm() {
			if (this.isSubmitDisabled) {
				this.showAlert({ type: 'error', message: 'Text cannot be empty' })
				return
			}

			this.field_errors = {}

			let result = this.fields.content

			this.mentions.forEach((item) => {
				const normalizedUsername = item.visible_username.replace(/\s+/g, '')
				const regex = new RegExp(
					`(^|\\s)@${this.escapeRegex(normalizedUsername)}(?=\\s|$)`,
					'g'
				)
				result = result.replace(regex, `$1@[${item.user_id}|${item.visible_username}]`)
			})

			const { id: commentId } = this.comment

			return this.editComment({ commentId, commentData: result })
		},
	},
}
</script>

<style lang="scss">
.comment-edit-form {
	font-family: 'Inter', sans-serif;
	font-style: normal;
	font-weight: 500;
	font-size: 20px;
	line-height: 24px;
	letter-spacing: 0.5px;
	background-color: #f3f3f6 !important;
	position: relative;

	&__text-outer {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	&__editable {
		max-width: 654px;
		width: 100%;
		min-height: 72px;
		color: #4a4a4a;
		border-radius: 8px;
		background-color: #ffffff;
		border: 1px solid #e2e2e2;
		padding: 12px;
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 500;
		line-height: 140%;
		outline: none;
		white-space: pre-wrap;
		word-break: break-all;
		cursor: text;
		transition: border-color 0.2s;
		&:empty::before {
			content: attr(data-placeholder);
			color: #9b9b9b;
			pointer-events: none;
		}

		&--active {
			border: 1px solid var(--secondary_color);
		}

		&:focus {
			border: 1px solid var(--secondary_color);
		}
	}

	&__buttons {
		display: flex;
		gap: 16px;

		&__save {
			width: 131px;
			height: 46px;
			border-radius: 8px;
			background-color: var(--primary_color);
			color: #ffffff;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;

			&:disabled {
				background-color: #d3d3d3;
				cursor: not-allowed;
				opacity: 0.7;
			}
		}

		&__cancel {
			width: 150px;
			height: 46px;
			border-radius: 8px;
			background-color: #ffffff;
			border: 1.5px solid var(--secondary_color);
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: var(--secondary_color);
		}
	}
}
.mention-chip {
	display: inline-block;
	color: var(--primary_color);
	border-radius: 4px;
	font-weight: 500;
	font-size: inherit;
	line-height: inherit;
	cursor: default;
	user-select: none;
}
.comment-edit-form .mention-bar {
	position: absolute;
	left: 12px;
	bottom: 90%;
	max-width: 654px !important;
	width: calc(100% - 24px);
	z-index: 10;
}

.v-label.v-label--active {
	top: 0 !important;
}
</style>
